import type { components } from '@/generated/contracts/api-v1';
import { fieldMap, requireId, type FieldSpec, type LegacyRow } from './fieldMap';

// asset_instances table; AssetInstanceDto.From in lureo-backend Data/Api/V1/SceneContent/AssetInstanceDtos.cs
export type AssetInstanceDto = components['schemas']['AssetInstanceDto'];
export type CreateAssetInstanceRequest = components['schemas']['CreateAssetInstanceRequest'];

const spec = {
  uniqueId: 'asset_instance_id',
  sceneUniqueId: 'scene_instance_id',
  assetTypeUniqueId: 'asset_type_id',
  userUniqueId: 'user_id',
  clipInstanceUniqueId: 'clip_instance_id',
  displayName: 'asset_instance_display_name',
  sourceName: 'asset_instance_source_name',
  category: 'asset_instance_category',
  tags: 'asset_instance_tags',
  details: { legacy: 'asset_instance_details', as: 'jsonText' },
  animations: { legacy: 'asset_instance_animations', as: 'jsonText' },
  unityJson: { legacy: 'asset_instance_unity_json', as: 'jsonText' },
  imgUrl: 'asset_instance_img_url',
  sourceUrl: 'asset_instance_source_url',
  pointCloudUrl: 'asset_instance_point_cloud_url',
  metadataUrl: 'asset_instance_metadata_url',
  referenceImageUrl: 'asset_instance_reference_image_url',
  position: 'asset_instance_position',
  rotation: 'asset_instance_rotation',
  scale: 'asset_instance_scale',
  longitude: 'asset_instance_longitude',
  latitude: 'asset_instance_latitude',
  altitude: 'asset_instance_altitude',
  beginTime: 'asset_instance_begin_time',
  endTime: 'asset_instance_end_time',
  displayOrder: 'asset_instance_display_order',
  isActive: 'asset_instance_is_active',
  isLocked: 'asset_instance_is_locked',
  isSelected: 'asset_instance_is_selected',
  isBillboarded: 'asset_instance_is_billboarded',
  hasPackedAlpha: 'asset_instance_has_packed_alpha',
  createdAt: 'created_at',
  updatedAt: 'updated_at',
} as const satisfies FieldSpec<AssetInstanceDto>;

const map = fieldMap<AssetInstanceDto, typeof spec>('Asset instance', spec);

export type LegacyAssetInstance = LegacyRow<typeof spec, AssetInstanceDto>;

/** updateAssetInstance / updateAssetInstances accept these. */
export const ASSET_INSTANCE_PATCH_FIELDS = [
  'assetTypeUniqueId', 'clipInstanceUniqueId', 'displayName', 'sourceName', 'category', 'tags', 'details', 'animations', 'unityJson',
  'imgUrl', 'sourceUrl', 'pointCloudUrl', 'metadataUrl', 'referenceImageUrl', 'position', 'rotation', 'scale',
  'longitude', 'latitude', 'altitude', 'beginTime', 'endTime', 'displayOrder', 'isActive', 'isLocked', 'isSelected',
  'isBillboarded', 'hasPackedAlpha',
] as const;
/** createAssetInstance accepts these. */
export const ASSET_INSTANCE_CREATE_FIELDS = ['uniqueId', 'sceneUniqueId', ...ASSET_INSTANCE_PATCH_FIELDS] as const;

export const assetInstanceToLegacy = (dto: AssetInstanceDto): LegacyAssetInstance => map.toLegacy(dto);

/** The AssetInstanceDto for a legacy row read from v1, for messages that carry the v1 shape. */
export const assetInstanceFromLegacy = (row: LegacyAssetInstance): AssetInstanceDto => map.toDto(row);

/**
 * createAssetInstance body from a legacy insert row; asset_instance_id is required. Clips and
 * keyframes created with the placement go in `clipInstances` / `keyframes` (clipInstanceCreateBody,
 * keyframeCreateBody), which the caller adds.
 */
export function assetInstanceCreateBody(row: Partial<LegacyAssetInstance>): CreateAssetInstanceRequest {
  const body = map.toBody(row, ASSET_INSTANCE_CREATE_FIELDS, ['user_id', 'created_at', 'updated_at']);
  return { ...body, uniqueId: requireId('Asset instance', 'asset_instance_id', body.uniqueId) };
}

/** updateAssetInstance body from a legacy partial update; updated_at is stamped by the server. */
export const assetInstancePatchBody = (changes: Partial<LegacyAssetInstance>) =>
  map.toBody(changes, ASSET_INSTANCE_PATCH_FIELDS, ['updated_at']);

/** One item of updateAssetInstances (POST /api/v1/asset-instances/batch). */
export const assetInstanceBatchItem = (id: string, changes: Partial<LegacyAssetInstance>) => ({ uniqueId: id, ...assetInstancePatchBody(changes) });
